import { Switch } from '@/components/ui/switch';

import type { WidgetPageContext, WidgetPageType } from '@repo/shared-types';

const PAGE_TYPE_OPTIONS: ReadonlyArray<{ value: WidgetPageType; label: string; hint: string }> = [
  { value: 'vehicle_detail', label: 'Single vehicle pages', hint: 'e.g. one car for sale' },
  { value: 'vehicle_listing', label: 'Vehicle listing pages', hint: 'search results, stock list' },
  { value: 'other', label: 'All other pages', hint: 'home, about, services…' },
];

const inputClass =
  'border-input placeholder:text-muted-foreground flex w-full rounded-md border bg-transparent px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none';

interface WidgetPageContextFieldsProps {
  value: WidgetPageContext;
  onChange: (value: WidgetPageContext) => void;
}

/** Per-configuration page awareness settings, in the Web Widget section. */
export const WidgetPageContextFields = ({ value, onChange }: WidgetPageContextFieldsProps) => {
  const update = (patch: Partial<WidgetPageContext>) => onChange({ ...value, ...patch });

  const togglePageType = (pageType: WidgetPageType, checked: boolean) =>
    update({
      page_types: checked
        ? [...new Set([...value.page_types, pageType])]
        : value.page_types.filter((type) => type !== pageType),
    });

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-sm font-medium">Page awareness</span>
          <p className="text-muted-foreground text-[11px]">
            The widget reads the page the visitor is on. The AI writes a welcome bubble about it
            (for example the car on screen), and the chat agent knows which page they mean. Nothing
            needs to be added to the website.
          </p>
        </div>
        <Switch
          checked={value.enabled}
          onCheckedChange={(enabled) => update({ enabled })}
          aria-label="Page awareness"
        />
      </div>

      {value.enabled && (
        <div className="space-y-4">
          <div className="space-y-2">
            <span className="text-sm font-medium">Show the bubble on</span>
            {PAGE_TYPE_OPTIONS.map((option) => (
              <label key={option.value} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={value.page_types.includes(option.value)}
                  onChange={(e) => togglePageType(option.value, e.target.checked)}
                />
                {option.label}
                <span className="text-muted-foreground text-[11px]">({option.hint})</span>
              </label>
            ))}
            <p className="text-muted-foreground text-[11px]">
              On other pages the normal welcome message shows. The chat still knows the page.
            </p>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <span className="text-sm font-medium">Page greeting on calls</span>
              <p className="text-muted-foreground text-[11px]">
                Widget calls start with a friendly AI greeting about the page (for example the car
                on screen) instead of the welcome message. If it is not ready, the welcome message
                plays.
              </p>
            </div>
            <Switch
              checked={value.call_greeting}
              onCheckedChange={(callGreeting) => update({ call_greeting: callGreeting })}
              aria-label="Page greeting on calls"
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="page-context-delay">
              Delay before the bubble (seconds)
            </label>
            <input
              id="page-context-delay"
              type="number"
              min={0}
              max={120}
              className={`${inputClass} h-10`}
              value={value.delay_seconds}
              onChange={(e) =>
                update({
                  delay_seconds: Math.min(
                    120,
                    Math.max(0, Math.round(Number(e.target.value) || 0)),
                  ),
                })
              }
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="page-context-instructions">
              Message style
            </label>
            <p className="text-muted-foreground text-[11px]">
              Optional tone guide for the AI, e.g. &quot;Friendly, offer a test drive&quot;.
            </p>
            <textarea
              id="page-context-instructions"
              maxLength={1000}
              className={`${inputClass} min-h-[60px]`}
              placeholder="Friendly and short. Offer a test drive or a part-exchange valuation."
              value={value.message_instructions}
              onChange={(e) => update({ message_instructions: e.target.value })}
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="page-context-exclude">
              Excluded pages
            </label>
            <p className="text-muted-foreground text-[11px]">
              One path per line. <code>*</code> matches anything, e.g. <code>/checkout/*</code>.
            </p>
            <textarea
              id="page-context-exclude"
              className={`${inputClass} min-h-[60px]`}
              placeholder={'/checkout/*\n/account/*'}
              value={value.exclude_paths.join('\n')}
              onChange={(e) => update({ exclude_paths: e.target.value.split('\n') })}
            />
          </div>
        </div>
      )}
    </div>
  );
};
